import { Injectable, signal, computed, inject, PLATFORM_ID } from '@angular/core';
import { fromEvent, map, debounceTime, startWith, of } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';

/** Modo efectivo del sidebar según viewport y override de usuario. */
export type SidebarMode = 'normal' | 'compact' | 'collapsed';

/**
 * Keys de localStorage:
 * - lt10.sidebar.mode: override de modo ('normal' | 'compact' | 'collapsed').
 * - lt10.sidebar.sectionOpen: id de la sección abierta en acordeón (compact).
 *
 * Modo: userOverride si existe; si no, auto por breakpoints:
 *   NORMAL   >= 1280px y height >= 800px
 *   COMPACTO (1024..1279px) o height < 800px
 *   COLAPSADO < 1024px
 * FontScale: NORMAL 1; COMPACTO 0.92 (h<720: 0.88); COLAPSADO 0.95. No se persiste.
 */
export const LT10_SIDEBAR_MODE = 'lt10.sidebar.mode';
export const LT10_SIDEBAR_SECTION_OPEN = 'lt10.sidebar.sectionOpen';

const W_NORMAL = 1280;
const H_NORMAL = 800;
const W_COMPACT_MAX = 1279;
const W_COMPACT_MIN = 1024;
const W_COLLAPSED = 1024;
const W_OVERLAY = 768;
const H_COMPACT_SMALL = 720;

@Injectable({ providedIn: 'root' })
export class SidebarUiService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly viewport$ = this.isBrowser
    ? fromEvent(window, 'resize').pipe(
        debounceTime(150),
        startWith(null),
        map(() => ({ w: window.innerWidth, h: window.innerHeight }))
      )
    : of({ w: 1920, h: 1080 });

  readonly viewport = toSignal(this.viewport$, { initialValue: this.isBrowser ? { w: window.innerWidth, h: window.innerHeight } : { w: 1920, h: 1080 } });

  /** Override de modo por usuario (persistido). null = usar auto. */
  private readonly userModeOverride = signal<SidebarMode | null>(this.readModeOverride());

  /** Modo efectivo: override si existe, sino auto por breakpoints. */
  readonly mode = computed<SidebarMode>(() => {
    const override = this.userModeOverride();
    if (override !== null) return override;
    return this.computeAutoMode(this.viewport());
  });

  /** Solo ancho < 768: overlay (drawer). */
  readonly overlay = computed<boolean>(() => this.viewport().w < W_OVERLAY);

  /** Escala tipográfica efectiva según viewport.
   * NORMAL: 1; COMPACTO: 0.92 (h < 720: 0.88); COLAPSADO: 0.95 (tooltips/flyout; rail sin labels).
   */
  readonly fontScale = computed<number>(() => {
    const { w, h } = this.viewport();
    const m = this.mode();
    if (m === 'normal') return 1;
    if (m === 'compact') return h < H_COMPACT_SMALL ? 0.88 : 0.92;
    return 0.95;
  });

  /** Sección abierta en acordeón (compact). Persistido. */
  private readonly sectionOpenId = signal<string | null>(this.readSectionOpen());

  readonly openSectionId = computed(() => this.sectionOpenId());

  setModeOverride(mode: SidebarMode | null): void {
    this.userModeOverride.set(mode);
    this.persistMode(mode);
  }

  setSectionOpen(id: string | null): void {
    this.sectionOpenId.set(id);
    this.persistSectionOpen(id);
  }

  private computeAutoMode({ w, h }: { w: number; h: number }): SidebarMode {
    if (w >= W_NORMAL && h >= H_NORMAL) return 'normal';
    if (w >= W_COLLAPSED && (w <= W_COMPACT_MAX || h < H_NORMAL)) return 'compact';
    return 'collapsed';
  }

  private readModeOverride(): SidebarMode | null {
    if (!this.isBrowser) return null;
    try {
      const v = localStorage.getItem(LT10_SIDEBAR_MODE);
      if (v === 'normal' || v === 'compact' || v === 'collapsed') return v;
    } catch {
      /* ignore */
    }
    return null;
  }

  private persistMode(mode: SidebarMode | null): void {
    if (!this.isBrowser) return;
    try {
      if (mode === null) localStorage.removeItem(LT10_SIDEBAR_MODE);
      else localStorage.setItem(LT10_SIDEBAR_MODE, mode);
    } catch {
      /* ignore */
    }
  }

  private readSectionOpen(): string | null {
    if (!this.isBrowser) return null;
    try {
      const v = localStorage.getItem(LT10_SIDEBAR_SECTION_OPEN);
      return v || null;
    } catch {
      return null;
    }
  }

  private persistSectionOpen(id: string | null): void {
    if (!this.isBrowser) return;
    try {
      if (id === null) localStorage.removeItem(LT10_SIDEBAR_SECTION_OPEN);
      else localStorage.setItem(LT10_SIDEBAR_SECTION_OPEN, id);
    } catch {
      /* ignore */
    }
  }
}
